import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { type PlatformAccount, type SuperadminPromotion } from './types';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/**
 * `GET /api/v1/admin/platform-accounts` - who holds platform power. Like every audited read of this app, it carries a stated reason (10
 * characters at least): the console's default one, or the one typed (`lib/consultation-reason.ts`).
 */
export function usePlatformAccountsQuery(
  reason: string,
): UseQueryResult<{ accounts: readonly PlatformAccount[] }, ApiError> {
  const { accessToken } = useSession();
  return useQuery({
    queryKey: ['platform-accounts', reason],
    queryFn: () =>
      apiRequest<{ accounts: readonly PlatformAccount[] }>(
        `/admin/platform-accounts?${new URLSearchParams({ reason }).toString()}`,
        { accessToken },
      ),
    enabled: accessToken !== null && reason.trim().length >= 10,
  });
}

/**
 * `POST /api/v1/admin/platform-accounts` - creates a platform ADMINISTRATOR (never a superadmin). The response carries the provisional
 * password: shown once by the caller, never cached (`gcTime: 0`).
 */
export function useCreateAdministratorMutation(): UseMutationResult<
  { userId: string; role: 'platform_admin'; temporaryPassword: string; expiresAt: string },
  ApiError,
  { email: string; displayName: string; reason: string; confirmPassword: string; locale?: string }
> {
  const { accessToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: (body) =>
      apiRequest<{
        userId: string;
        role: 'platform_admin';
        temporaryPassword: string;
        expiresAt: string;
      }>('/admin/platform-accounts', { method: 'POST', body, accessToken }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform-accounts'] });
    },
  });
}

export type AccountAction = 'revoke-role' | 'disable' | 'enable';

/** `POST /api/v1/admin/platform-accounts/:id/{revoke-role|disable|enable}`. The password travels in the body only and is not kept. */
export function useAccountActionMutation(): UseMutationResult<
  { status: string },
  ApiError,
  { userId: string; action: AccountAction; reason: string; confirmPassword: string }
> {
  const { accessToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: ({ userId, action, reason, confirmPassword }) =>
      apiRequest<{ status: string }>(`/admin/platform-accounts/${userId}/${action}`, {
        method: 'POST',
        body: { reason, confirmPassword },
        accessToken,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform-accounts'] });
    },
  });
}

/** `GET /api/v1/admin/superadmin-promotions` - the recent requests (a superadmin only). */
export function usePromotionsQuery(
  reason: string,
): UseQueryResult<{ promotions: readonly SuperadminPromotion[] }, ApiError> {
  const { accessToken } = useSession();
  return useQuery({
    queryKey: ['superadmin-promotions', reason],
    queryFn: () =>
      apiRequest<{ promotions: readonly SuperadminPromotion[] }>(
        `/admin/superadmin-promotions?${new URLSearchParams({ reason }).toString()}`,
        { accessToken },
      ),
    enabled: accessToken !== null && reason.trim().length >= 10,
  });
}

/** `POST /api/v1/admin/superadmin-promotions` - one superadmin REQUESTS; a different one must confirm. */
export function useRequestPromotionMutation(): UseMutationResult<
  { requestId: string },
  ApiError,
  { userId: string; reason: string; confirmPassword: string }
> {
  const { accessToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: (body) =>
      apiRequest<{ requestId: string }>('/admin/superadmin-promotions', {
        method: 'POST',
        body,
        accessToken,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['superadmin-promotions'] });
    },
  });
}

/** Confirm (a DIFFERENT superadmin) or reject / cancel a pending request. */
export function useDecidePromotionMutation(): UseMutationResult<
  { status: string },
  ApiError,
  { id: string; decision: 'confirm' | 'reject'; reason: string; confirmPassword: string }
> {
  const { accessToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: ({ id, decision, reason, confirmPassword }) =>
      apiRequest<{ status: string }>(`/admin/superadmin-promotions/${id}/${decision}`, {
        method: 'POST',
        body: { reason, confirmPassword },
        accessToken,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['superadmin-promotions'] });
      void queryClient.invalidateQueries({ queryKey: ['platform-accounts'] });
    },
  });
}
